import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Trash2, Shield, Plus, Plug, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import { getAllTimezones, getCurrentOffset } from "@/lib/timezones";
import { useAuth } from "@/contexts/AuthContext";
import CustomFieldsManager from "@/components/CustomFieldsManager";

const DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD MMM YYYY"];
const ALL_TZ = getAllTimezones();

export default function Settings() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [creds, setCreds] = useState(null);
  const [credsForm, setCredsForm] = useState({
    ms_client_id: "", ms_client_secret: "", ms_tenant_id: "", ms_redirect_uri: "", ms_mock_mode: false,
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "member" });

  const load = async () => {
    try {
      const [s, u, c] = await Promise.all([
        api.get("/settings"),
        api.get("/users"),
        api.get("/microsoft/credentials"),
      ]);
      setSettings(s.data);
      setUsers(u.data);
      setCreds(c.data);
      setCredsForm({
        ms_client_id: c.data.ms_client_id || "",
        ms_client_secret: "",
        ms_tenant_id: c.data.ms_tenant_id || "",
        ms_redirect_uri: c.data.ms_redirect_uri || "",
        ms_mock_mode: c.data.ms_mock_mode === true,
      });
    } catch {}
  };
  useEffect(() => { load(); }, []);

  if (!settings) return null;

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", { ...settings, ...patch });
      setSettings(data);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const setRole = async (uid, role) => {
    try {
      await api.put(`/users/${uid}/role?role=${role}`);
      toast.success("Role updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("User removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || newUser.password.length < 6) {
      toast.error("Name, email and password (6+ chars) required");
      return;
    }
    try {
      await api.post("/users", newUser);
      toast.success(`User '${newUser.email}' added`);
      setAddUserOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "member" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const saveCreds = async () => {
    try {
      await api.put("/microsoft/credentials", credsForm);
      toast.success("Microsoft credentials saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/microsoft/test-connection", credsForm);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || "Test failed" });
    }
    setTesting(false);
  };

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Configuration
        </p>
        <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          Settings
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-10 xl:grid-cols-2">
        {/* General */}
        <Section title="General" sub="Global identity and locale">
          <Field label="Company name">
            <Input
              value={settings.company_name || ""}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              onBlur={() => save({ company_name: settings.company_name })}
              className="rounded-none border-neutral-300"
              data-testid="settings-company-input"
            />
          </Field>
          <Field label={`Timezone (${ALL_TZ.length} zones)`}>
            <TimezoneSelect
              value={settings.timezone}
              onChange={(v) => save({ timezone: v })}
            />
          </Field>
          <Field label="Date format">
            <Select value={settings.date_format} onValueChange={(v) => save({ date_format: v })}>
              <SelectTrigger className="rounded-none border-neutral-300" data-testid="settings-date-format-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {DATE_FORMATS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </Section>

        {/* Sync direction */}
        <Section title="Sync Direction" sub="How edits flow between this app and Microsoft">
          <div className="flex items-center justify-between border border-neutral-200 p-4">
            <div className="mr-3">
              <p className="text-sm font-medium">Two-way sync</p>
              <p className="text-xs text-neutral-600">
                When ON, local edits push back to Microsoft Graph. When OFF, edits stay local.
              </p>
            </div>
            <Switch
              checked={!!settings.two_way_sync}
              onCheckedChange={(v) => save({ two_way_sync: v })}
              data-testid="settings-two-way-sync-toggle"
            />
          </div>
          <div className="flex items-center justify-between border border-neutral-200 p-4">
            <div>
              <p className="text-sm font-medium">Microsoft connection</p>
              <p className="text-xs text-neutral-600">
                {settings.microsoft_connected ? "Connected to Microsoft 365" : "Not connected"}
                {creds?.ms_mock_mode ? " · MOCK MODE" : ""}
              </p>
            </div>
            <Badge
              variant="outline"
              className="rounded-none border-neutral-300 text-[10px] uppercase"
              style={{ color: settings.microsoft_connected ? "#0A7029" : "#D83B01", fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {settings.microsoft_connected ? "Live" : "Offline"}
            </Badge>
          </div>
          <p className="text-[11px] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            Last sync: {settings.last_sync ? new Date(settings.last_sync).toLocaleString() : "—"}
          </p>
        </Section>

        {/* Microsoft Integration */}
        <Section
          title="Microsoft Integration"
          sub="Azure AD app registration credentials"
          className="xl:col-span-2"
        >
          <div className="border border-neutral-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Plug size={18} className="text-[#002FA7]"/>
                <div>
                  <p className="text-sm font-medium">Connect to Microsoft 365</p>
                  <p className="text-xs text-neutral-600">Use mock data while developing, then flip OFF to call real Microsoft Graph.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  Mock mode
                </span>
                <Switch
                  checked={!!credsForm.ms_mock_mode}
                  onCheckedChange={(v) => setCredsForm({ ...credsForm, ms_mock_mode: v })}
                  data-testid="ms-mock-mode-toggle"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Client ID (Application ID)">
                <Input
                  value={credsForm.ms_client_id}
                  onChange={(e) => setCredsForm({ ...credsForm, ms_client_id: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="rounded-none border-neutral-300 font-mono text-xs"
                  data-testid="ms-client-id-input"
                />
              </Field>
              <Field label="Tenant ID (Directory ID)">
                <Input
                  value={credsForm.ms_tenant_id}
                  onChange={(e) => setCredsForm({ ...credsForm, ms_tenant_id: e.target.value })}
                  placeholder="contoso.onmicrosoft.com or GUID"
                  className="rounded-none border-neutral-300 font-mono text-xs"
                  data-testid="ms-tenant-id-input"
                />
              </Field>
              <Field label={`Client Secret ${creds?.ms_client_secret_set ? "(saved)" : ""}`}>
                <Input
                  type="password"
                  value={credsForm.ms_client_secret}
                  onChange={(e) => setCredsForm({ ...credsForm, ms_client_secret: e.target.value })}
                  placeholder={creds?.ms_client_secret_set ? "•••• (leave blank to keep)" : "client secret value"}
                  className="rounded-none border-neutral-300 font-mono text-xs"
                  data-testid="ms-client-secret-input"
                />
              </Field>
              <Field label="Redirect URI">
                <Input
                  value={credsForm.ms_redirect_uri}
                  onChange={(e) => setCredsForm({ ...credsForm, ms_redirect_uri: e.target.value })}
                  placeholder={`${window.location.origin}/api/auth/microsoft/callback`}
                  className="rounded-none border-neutral-300 font-mono text-xs"
                  data-testid="ms-redirect-uri-input"
                />
              </Field>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={testConnection}
                disabled={testing}
                variant="outline"
                className="rounded-none border-neutral-300"
                data-testid="test-connection-button"
              >
                {testing ? "Testing…" : "Test connection"}
              </Button>
              <Button
                onClick={saveCreds}
                disabled={!isAdmin}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
                data-testid="save-ms-credentials-button"
              >
                Save credentials
              </Button>
              {testResult && (
                <div
                  className={`flex items-center gap-2 border px-3 py-1.5 text-xs ${
                    testResult.ok ? "border-[#0A7029] text-[#0A7029]" : "border-[#C50F1F] text-[#C50F1F]"
                  }`}
                  data-testid="test-connection-result"
                >
                  {testResult.ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
                  <span>{testResult.message}</span>
                </div>
              )}
            </div>

            {/* Step-by-step guide */}
            <div className="mt-6 border-t border-neutral-200 pt-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                Need help? Step-by-step guide
              </p>
              <Accordion type="single" collapsible className="mt-2" data-testid="ms-setup-guide">
                <GuideStep value="s1" title="1. Register an application in Azure AD">
                  <p>Go to the Azure portal → <strong>Microsoft Entra ID</strong> (formerly Azure Active Directory) → <strong>App registrations</strong> → <strong>New registration</strong>.</p>
                  <ul className="ml-4 list-disc">
                    <li><strong>Name</strong>: e.g. <code>Organogram App</code></li>
                    <li><strong>Supported account types</strong>: choose <em>Single tenant</em> for your company only.</li>
                    <li><strong>Redirect URI</strong>: select <em>Web</em>, then paste the Redirect URI shown above.</li>
                  </ul>
                  <ExternalA href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade">Open Azure App registrations →</ExternalA>
                </GuideStep>
                <GuideStep value="s2" title="2. Copy the Application (Client) ID and Tenant ID">
                  <p>On the app's <strong>Overview</strong> page you'll see:</p>
                  <ul className="ml-4 list-disc">
                    <li><strong>Application (client) ID</strong> → paste into the <em>Client ID</em> field above.</li>
                    <li><strong>Directory (tenant) ID</strong> → paste into the <em>Tenant ID</em> field above.</li>
                  </ul>
                </GuideStep>
                <GuideStep value="s3" title="3. Create a client secret">
                  <p>Go to <strong>Certificates &amp; secrets → New client secret</strong>. Choose an expiry (24 months recommended), copy the <strong>Value</strong> immediately (it's only shown once), and paste into the <em>Client Secret</em> field above.</p>
                </GuideStep>
                <GuideStep value="s4" title="4. Grant API permissions">
                  <p>Under <strong>API permissions → Add a permission → Microsoft Graph</strong>, add the following (Application permissions for sync, Delegated for SSO):</p>
                  <ul className="ml-4 list-disc font-mono text-[11px]">
                    <li>User.Read (Delegated) — sign-in</li>
                    <li>User.ReadBasic.All (Delegated) — directory read</li>
                    <li>User.Read.All (Application) — pull users</li>
                    <li>User.ReadWrite.All (Application) — push edits back (only if two-way sync is ON)</li>
                  </ul>
                  <p>Click <strong>Grant admin consent</strong>. A tenant admin has to approve.</p>
                </GuideStep>
                <GuideStep value="s5" title="5. Add users to this platform first">
                  <p className="rounded-none border-l-2 border-[#D83B01] bg-orange-50 p-3 text-xs">
                    <strong>Important:</strong> Microsoft SSO is only allowed for users that already exist in this app. An administrator must add each user in <em>Users &amp; Roles</em> below before they can sign in with Microsoft. Otherwise the SSO callback returns a 403 with “User is not registered on this platform”.
                  </p>
                </GuideStep>
                <GuideStep value="s6" title="6. Save credentials and test the connection">
                  <p>Paste all four values, click <strong>Save credentials</strong>, then <strong>Test connection</strong>. Once you see “Connected to Microsoft Graph successfully”, turn <em>Mock mode</em> OFF and use <em>Sync from Microsoft</em> from the Organogram page.</p>
                </GuideStep>
              </Accordion>
            </div>
          </div>
        </Section>

        {/* Custom Fields */}
        <Section title="Custom Fields" sub="Additional data captured on every employee" className="xl:col-span-2">
          <CustomFieldsManager isAdmin={isAdmin}/>
        </Section>

        {/* Users & Roles */}
        <Section title="Users & Roles" sub="Access control for this workspace" className="xl:col-span-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-600">
              Add platform users here. Microsoft SSO is only granted to users that already exist.
            </p>
            <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
              <DialogTrigger asChild>
                <Button
                  disabled={!isAdmin}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
                  data-testid="add-user-button"
                >
                  <Plus size={14} className="mr-2"/>
                  Add user
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>Add platform user</DialogTitle>
                  <DialogDescription className="text-xs text-neutral-600">
                    Newly created users can sign in immediately — either with their password, or via Microsoft SSO (their MS email must match).
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Field label="Full name">
                    <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="rounded-none" data-testid="new-user-name-input"/>
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="rounded-none" data-testid="new-user-email-input"/>
                  </Field>
                  <Field label="Temporary password (6+ chars)">
                    <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="rounded-none" data-testid="new-user-password-input"/>
                  </Field>
                  <Field label="Role">
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="rounded-none" data-testid="new-user-role-select"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <DialogFooter>
                  <Button onClick={createUser} className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]" data-testid="create-user-submit">
                    Create user
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-neutral-200">
            <ul>
              {users.map((u) => (
                <li key={u.id} className="flex items-center gap-3 border-b border-neutral-100 p-3 last:border-0" data-testid={`user-row-${u.id}`}>
                  <div className="flex h-8 w-8 items-center justify-center bg-[#002FA7] text-[10px] text-white">
                    {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{u.name || "—"}</p>
                    <p className="truncate text-xs text-neutral-600">{u.email}</p>
                  </div>
                  <Badge variant="outline" className="rounded-none border-neutral-300 text-[10px] uppercase" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {u.provider}
                  </Badge>
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)} disabled={!isAdmin}>
                    <SelectTrigger className="h-8 w-28 rounded-none border-neutral-300 text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => deleteUser(u.id)} disabled={!isAdmin || u.id === me?.id} className="text-neutral-400 hover:text-[#C50F1F] disabled:opacity-30" data-testid={`delete-user-${u.id}`} aria-label="Delete user">
                    <Trash2 size={14}/>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <p className="flex items-center gap-2 text-[11px] text-neutral-500">
            <Shield size={11}/> {isAdmin ? "You're signed in as admin." : "Only admins can modify users or save Microsoft credentials."}
          </p>
        </Section>
      </div>
    </div>
  );
}

function TimezoneSelect({ value, onChange }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_TZ;
    return ALL_TZ.filter((tz) => tz.toLowerCase().includes(q));
  }, [query]);

  const offset = getCurrentOffset(value);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-none border-neutral-300" data-testid="settings-timezone-select">
        <SelectValue>
          <span className="font-mono text-xs">{value}</span>
          {offset && <span className="ml-2 text-[10px] text-neutral-500">{offset}</span>}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-96">
        <div className="sticky top-0 z-10 border-b border-neutral-200 bg-white p-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search… e.g. Johannesburg"
            onKeyDown={(e) => e.stopPropagation()}
            className="h-8 rounded-none border-neutral-300 text-xs"
            data-testid="timezone-search-input"
          />
        </div>
        {filtered.slice(0, 200).map((tz) => (
          <SelectItem key={tz} value={tz} className="font-mono text-xs">
            <div className="flex w-full items-center justify-between gap-3">
              <span>{tz}</span>
              <span className="text-[10px] text-neutral-500">{getCurrentOffset(tz)}</span>
            </div>
          </SelectItem>
        ))}
        {filtered.length > 200 && (
          <p className="px-3 py-2 text-[11px] text-neutral-500">…{filtered.length - 200} more — refine search</p>
        )}
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-[11px] text-neutral-500">No timezone matches.</p>
        )}
      </SelectContent>
    </Select>
  );
}

function Section({ title, sub, children, className = "" }) {
  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {sub}
        </p>
        <h2 className="mt-1 text-xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          {title}
        </h2>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</Label>
      {children}
    </div>
  );
}

function GuideStep({ value, title, children }) {
  return (
    <AccordionItem value={value} className="border-neutral-200" data-testid={`guide-${value}`}>
      <AccordionTrigger className="text-sm hover:no-underline">{title}</AccordionTrigger>
      <AccordionContent className="space-y-2 text-xs text-neutral-700">{children}</AccordionContent>
    </AccordionItem>
  );
}

function ExternalA({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#002FA7] hover:underline">
      {children} <ExternalLink size={11}/>
    </a>
  );
}
